// A7 Pengendalian Kontrak (stage pelaksanaan_kontrak): the contract's
// monitoring record. The form starts from the latest entry; each Simpan
// pemantauan appends a new entry to the history (newest first). Deviasi is
// realisasi minus rencana, an internal indicator only: delay, penalties
// and time extensions follow the contract and the authorized officials.
import { type FormEvent, useEffect, useId, useState } from "react";
import * as API from "../../api";
import { formatDate, formatRupiah } from "../../lib/format";
import { Badge, Field, Notice, SkeletonRows, useAsync } from "../../ui/components";
import * as Work from "./api";
import type { ContractEntry, ContractView } from "./api";
import { WorkSection, parseNumber } from "./shared";

interface FormState {
  contractNo: string;
  contractValue: string;
  startDate: string;
  endDate: string;
  plan: string;
  actual: string;
  paid: string;
  reportDate: string;
  risk: string;
  action: string;
}

const today = () => new Date().toISOString().slice(0, 10);
const show = (n: number) => String(n).replace(".", ",");

function formOf(entry: ContractEntry | null): FormState {
  return {
    contractNo: entry?.contractNo ?? "",
    contractValue: entry ? show(entry.contractValue) : "",
    startDate: entry?.startDate ?? "",
    endDate: entry?.endDate ?? "",
    plan: entry ? show(entry.plan) : "0",
    actual: entry ? show(entry.actual) : "0",
    paid: entry ? show(entry.paid) : "0",
    // A new update is usually today's.
    reportDate: today(),
    risk: entry?.risk ?? "",
    action: entry?.action ?? "",
  };
}

/** Realisasi minus rencana, one decimal (the API's rounding). */
export function deviationOf(plan: number, actual: number): number {
  return Math.round((actual - plan) * 10) / 10;
}

const signed = (n: number) => `${n > 0 ? "+" : ""}${show(n)}%`;

export function PengendalianKontrak({ projectId }: { projectId: string }) {
  const uid = useId();
  const contract = useAsync<ContractView>(() => Work.getContract(projectId), [projectId]);
  const [form, setForm] = useState<FormState>(formOf(null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [all, setAll] = useState(false);

  useEffect(() => {
    if (contract.data) setForm(formOf(contract.data.current));
  }, [contract.data]);

  const set = (key: keyof FormState) => (e: { currentTarget: { value: string } }) => {
    const value = e.currentTarget.value;
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const plan = parseNumber(form.plan);
  const actual = parseNumber(form.actual);
  const value = form.contractValue.trim() ? parseNumber(form.contractValue) : 0;
  const paid = form.paid.trim() ? parseNumber(form.paid) : 0;
  const pctBad = (n: number) => Number.isNaN(n) || n < 0 || n > 100;
  const amountBad = (n: number) => Number.isNaN(n) || n < 0;
  const datesBad = !!form.startDate && !!form.endDate && form.endDate < form.startDate;
  const invalid = pctBad(plan) || pctBad(actual) || amountBad(value) || amountBad(paid) || datesBad;
  const dev = pctBad(plan) || pctBad(actual) ? null : deviationOf(plan, actual);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (invalid) return;
    setBusy(true);
    setError(null);
    try {
      contract.set(
        await Work.recordContract(projectId, {
          contractNo: form.contractNo.trim(),
          contractValue: value,
          startDate: form.startDate || null,
          endDate: form.endDate || null,
          plan,
          actual,
          paid,
          reportDate: form.reportDate || null,
          risk: form.risk.trim(),
          action: form.action.trim(),
        }),
      );
      setSaved(true);
    } catch (err) {
      setError(API.errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  let body;
  if (contract.loading && !contract.data) body = <SkeletonRows count={4} label="Memuat pengendalian kontrak" />;
  else if (contract.error && !contract.data) {
    body = (
      <Notice
        tone="danger"
        title="Data kontrak belum termuat"
        action={
          <button type="button" className="btn btn-secondary" onClick={contract.reload}>
            Coba lagi
          </button>
        }
      >
        <p>{API.errorMessage(contract.error)}</p>
      </Notice>
    );
  } else if (contract.data) {
    const history = contract.data.history;
    const shown = all ? history : history.slice(0, 8);
    body = (
      <>
        <form className="wk-card" onSubmit={(e) => void submit(e)} noValidate>
          <div className="wk-fields wk-fields-2">
            <Field id={`${uid}-no`} label="Nomor kontrak / SPK">
              <input id={`${uid}-no`} value={form.contractNo} maxLength={100} placeholder="Nomor yang telah sah" onChange={set("contractNo")} />
            </Field>
            <Field id={`${uid}-value`} label="Nilai kontrak (Rp)" error={amountBad(value) ? "Isi angka 0 atau lebih." : null}>
              <input id={`${uid}-value`} inputMode="decimal" value={form.contractValue} aria-invalid={amountBad(value) || undefined} onChange={set("contractValue")} />
            </Field>
            <Field id={`${uid}-start`} label="Tanggal mulai">
              <input id={`${uid}-start`} type="date" value={form.startDate} onChange={set("startDate")} />
            </Field>
            <Field id={`${uid}-end`} label="Tanggal akhir sesuai kontrak" error={datesBad ? "Tanggal akhir tidak boleh lebih awal dari tanggal mulai." : null}>
              <input id={`${uid}-end`} type="date" value={form.endDate} aria-invalid={datesBad || undefined} onChange={set("endDate")} />
            </Field>
            <Field id={`${uid}-plan`} label="Progres rencana kumulatif (%)" error={pctBad(plan) ? "Isi angka 0 sampai 100." : null}>
              <input id={`${uid}-plan`} inputMode="decimal" value={form.plan} aria-invalid={pctBad(plan) || undefined} onChange={set("plan")} />
            </Field>
            <Field id={`${uid}-actual`} label="Progres realisasi kumulatif (%)" error={pctBad(actual) ? "Isi angka 0 sampai 100." : null}>
              <input id={`${uid}-actual`} inputMode="decimal" value={form.actual} aria-invalid={pctBad(actual) || undefined} onChange={set("actual")} />
            </Field>
            <Field id={`${uid}-paid`} label="Pembayaran diterima kumulatif (Rp)" error={amountBad(paid) ? "Isi angka 0 atau lebih." : null}>
              <input id={`${uid}-paid`} inputMode="decimal" value={form.paid} aria-invalid={amountBad(paid) || undefined} onChange={set("paid")} />
            </Field>
            <Field id={`${uid}-date`} label="Tanggal pemantauan">
              <input id={`${uid}-date`} type="date" value={form.reportDate} onChange={set("reportDate")} />
            </Field>
          </div>
          <div className="wk-fields">
            <Field id={`${uid}-risk`} label="Kendala dan risiko aktual" wide>
              <textarea id={`${uid}-risk`} rows={2} maxLength={1_600} value={form.risk} placeholder="Misalnya: pengiriman material terlambat; lampirkan bukti kejadian" onChange={set("risk")} />
            </Field>
            <Field id={`${uid}-action`} label="Rencana tindak lanjut dan PIC" wide>
              <textarea id={`${uid}-action`} rows={2} maxLength={1_600} value={form.action} placeholder="Tindakan perbaikan, tanggal target, PIC, dokumen pemberitahuan" onChange={set("action")} />
            </Field>
          </div>
          <div className="wk-actions">
            <span className="wk-deviation">
              Deviasi{" "}
              {dev === null ? (
                <Badge>Periksa angka</Badge>
              ) : (
                <Badge tone={dev < 0 ? "warning" : "success"}>
                  <span className="num">{signed(dev)}</span>
                </Badge>
              )}
            </span>
            <button type="submit" className="btn btn-primary" disabled={invalid || busy}>
              {busy ? "Menyimpan..." : "Simpan pemantauan"}
            </button>
            {saved ? <span className="wk-hint">Tersimpan sebagai pembaruan baru.</span> : null}
          </div>
        </form>

        <div className="wk-card">
          <h3 className="wk-subtitle">Riwayat pemantauan ({history.length})</h3>
          {history.length === 0 ? (
            <p className="wk-empty">Belum ada pemantauan yang disimpan.</p>
          ) : (
            <ol className="wk-history" aria-label="Riwayat pemantauan, terbaru di atas">
              {shown.map((h) => (
                <li key={h.loggedAt} className="wk-history-item">
                  <strong className="num">{h.reportDate ? formatDate(h.reportDate) : formatDate(h.loggedAt)}</strong>
                  <span className="num">
                    Rencana {show(h.plan)}%, realisasi {show(h.actual)}%, deviasi {signed(h.deviation)}
                  </span>
                  <span className="wk-history-meta num">Dibayar {formatRupiah(h.paid)}</span>
                  {h.risk ? <span className="wk-history-meta">Kendala: {h.risk}</span> : null}
                </li>
              ))}
            </ol>
          )}
          {history.length > 8 ? (
            <button type="button" className="btn btn-secondary" onClick={() => setAll((a) => !a)}>
              {all ? "Tampilkan lebih sedikit" : `Tampilkan semua (${history.length})`}
            </button>
          ) : null}
        </div>
        <p className="wk-note">Deviasi negatif adalah indikator pemantauan internal, bukan keputusan mengenai keterlambatan, denda, atau perpanjangan waktu. Semua itu mengikuti ketentuan kontrak dan penilaian pejabat berwenang.</p>
      </>
    );
  }

  return (
    <WorkSection id="pengendalian-kontrak" title="Pengendalian Kontrak" lead="Rekam baseline, realisasi, deviasi progres, pembayaran, dan tindak lanjut. Semua angka berasal dari isian dan bukti perusahaan.">
      {body}
      {error ? (
        <Notice tone="danger" title="Pemantauan belum tersimpan">
          <p>{error}</p>
        </Notice>
      ) : null}
    </WorkSection>
  );
}
